import React, { useState, useEffect } from "react";
import Button from "./Button";
import { useStateContext } from "../contexts/ContextProvider";
import ProgressBar from "./ProgressBar";
import ImageUploader from "./ImageUploader";
import { apiCreateRoom } from "../services/apiService";

const initialErrorState = {
  roomName: "",
  rent: "",
  securityDep: "",
  shortDesc: "",
  longDesc: "",
};

const AddRoom = (props) => {
  const { currentProperty, cancelAddRoom, handleModal } = props;
  const { user, rooms, setRooms, setAddress } = useStateContext();
  const [page, setPage] = useState(1);
  const [isLoading, setIsLoading] = useState(false);
  const [duplicateNameError, setDuplicateNameError] = useState(false);
  const [roomError, setRoomError] = useState(initialErrorState);
  const currentPropertyRooms = rooms.filter(
    (x) => x.propertyId === currentProperty.id
  );

  const [room, setRoom] = useState({
    landlordId: user.id,
    propertyId: currentProperty.id,
    propertyNeighborHood: currentProperty.neighborhood,
    propertyCity: currentProperty.city,
    propertyState: currentProperty.state,
    propertyZip: currentProperty.zipCode,
    propertyLat: currentProperty.latitude,
    propertyLong: currentProperty.longitude,
    roomName: `Room-${currentPropertyRooms.length + 1}`,
    rent: "",
    securityDep: "",
    shortDesc: "",
    longDesc: "",
    miscDesc1: "",
    miscDesc2: "",
    images: [],
    isPublished: false,
    isFurnished: false,
    couplesOk: false,
    privateBath: false,
    smokingOk: false,
    cannabisOk: false,
    maleOnly: false,
    femaleOnly: false,
    isCoEd: true,
    catsOk: false,
    dogsOk: false,
    lgbtFriendly: false,
    laundry: false,
    wifi: false,
    parking: false,
    isAccessible: false,
    available: "",
  });

  const pages = 6;

  useEffect(() => {
    setAddress(currentProperty);
  });

  const handleAddRoom = async () => {
    setIsLoading(true);
    const response = await apiCreateRoom(room);
    if (response.hasError) {
      setIsLoading(false);
      console.log(response.error.response.data.error);
      return;
    } else {
      setRooms((prevState) => [...prevState, response.data]);
      handleModal("addRoom");
      setIsLoading(false);
    }
  };

  const handleChange = (event) => {
    const { name, value } = event.target;
    const reg = new RegExp("^[0-9]*$"); // is integers or empty?
    let safeValue;

    if (name === "roomName") {
      // check for repeat names
      if (
        currentPropertyRooms.find(
          (item) => item.roomName.toLowerCase() === value.trim().toLowerCase()
        )
      ) {
        setDuplicateNameError(true);
      } else {
        setDuplicateNameError(false);
      }
    }

    if (name === "rent" || name === "securityDep") {
      if (reg.test(value)) {
        safeValue = Number(value);
      } else {
        return;
      }
    } else {
      safeValue = value;
    }

    setRoom((prevState) => ({ ...prevState, [name]: safeValue }));
  };

  const handleCheckbox = (event) => {
    const { name, checked } = event.target;

    if (name === "maleOnly") {
      setRoom((prevState) => ({
        ...prevState,
        femaleOnly: checked ? false : true,
        isCoEd: false,
      }));
    }
    if (name === "femaleOnly") {
      setRoom((prevState) => ({
        ...prevState,
        isCoEd: checked ? false : true,
        maleOnly: false,
      }));
    }
    if (name === "isCoEd") {
      setRoom((prevState) => ({
        ...prevState,
        maleOnly: checked ? false : true,
        femaleOnly: false,
      }));
    }
    // if (name === "isCoEd" && checked === false) {
    //   setRoom((prevState) => ({
    //     ...prevState,
    //     maleOnly: true,
    //     femaleOnly: false,
    //   }));
    // }

    setRoom((prevState) => ({ ...prevState, [name]: checked }));
  };

  const nextPage = () => {
    if (hasRoomErrors() || isDuplicateName()) {
      return;
    } else {
      setPage(page + 1);
    }
  };

  const prevPage = () => {
    setPage(page - 1);
  };

  // check if pre-populated name is a repeat
  const isDuplicateName = () => {
    if (
      currentPropertyRooms.find(
        (item) => item.roomName === room.roomName.toLowerCase()
      )
    ) {
      activateError("roomName");
      setDuplicateNameError(true);
      return true;
    }
    return false;
  };

  const hasRoomErrors = () => {
    if (page === 1 && (room.roomName === "" || duplicateNameError)) {
      activateError("roomName");
    } else if (page === 2 && !room.rent) {
      activateError("rent");
    } else if (page === 2 && !room.securityDep) {
      activateError("securityDep");
    } else if (page === 3 && room.shortDesc === "") {
      activateError("shortDesc");
    } else if (page === 4 && room.longDesc === "") {
      activateError("longDesc");
    } else {
      return false;
    }
    return true;
  };

  const activateError = (item) => {
    setRoomError((prevState) => ({
      ...prevState,
      [item]: "animate-shake text-red-600 border-red-600",
    }));
    setTimeout(() => {
      setRoomError((prevState) => ({
        ...prevState,
        [item]: "",
      }));
    }, 500);
    clearTimeout();
  };

  return (
    <div className="w-screen h-screen fixed bg-white mt-0 mb-40 z-30 overflow-y-scroll">
      <ProgressBar pages={pages} currentPage={page} />
      <form
        className="grid justify-items-center"
        onSubmit={(e) => {
          e.preventDefault();
          page === pages ? handleAddRoom() : nextPage();
        }}
      >
        {page === 1 && (
          <>
            <div
              className={`text-lg text-justified mt-5 mb-5 px-10 ${roomError.roomName}`}
            >
              What do you want to call this room?
            </div>
            <label className="grid">
              <input
                id="room-name"
                name="roomName"
                type="text"
                value={room.roomName}
                autoFocus={true}
                placeholder="Room Name"
                className={`border-gray-400 rounded-md border-1 ${roomError.roomName} p-1`}
                onChange={handleChange}
              />
              {duplicateNameError ? (
                <span className="text-red-500">This name is taken.</span>
              ) : (
                ""
              )}
            </label>
          </>
        )}
        {page === 2 && (
          <>
            <div
              className={`text-lg text-justified mt-5 mb-1 px-10 ${roomError.rent}`}
            >
              What is the rent for this room?
              <span className="text-red-500">*</span>
            </div>
            <label className="grid">
              <div className="flex relative">
                <div className="absolute left-2">$</div>
                <input
                  id="rent"
                  name="rent"
                  type="tel"
                  maxLength={6}
                  placeholder={0}
                  value={room.rent}
                  className={`w-40 text-right border-gray-400 rounded-md border-1 pr-8 ${roomError.rent}`}
                  onChange={handleChange}
                />
                <div className="absolute right-2 top-.05">.00</div>
              </div>
            </label>
            {room.rent !== "" && (
              <>
                <div className="text-lg text-justified mt-10 px-10">
                  Is there a security deposit?
                </div>
                <label className="grid">
                  <div className="flex relative">
                    <div className="absolute left-2">$</div>
                    <input
                      id="security-dep"
                      name="securityDep"
                      type="tel"
                      maxLength={6}
                      placeholder={0}
                      value={room.securityDep}
                      className="w-40 text-right border-gray-400 rounded-md border-1 pr-8"
                      onChange={handleChange}
                    />
                    <div className="absolute right-2 top-.05">.00</div>
                  </div>
                </label>
              </>
            )}
          </>
        )}
        {page === 3 && (
          <>
            <div
              className={`text-lg text-justified mt-5 mb-2 px-8 ${roomError.shortDesc}`}
            >
              Provide a short descriptive title.
            </div>
            <label className="grid ">
              <input
                id="short-desc"
                name="shortDesc"
                type="text"
                maxLength={70}
                autoFocus={true}
                placeholder="Ex: Large room w/ private bathroom."
                value={room.shortDesc}
                className={`w-80 border-gray-400 rounded-md border-1 ${roomError.shortDesc} p-1`}
                onChange={handleChange}
              />
            </label>
          </>
        )}
        {page === 4 && (
          <>
            <div
              className={`text-lg text-left mt-5 mb-5 px-8 ${roomError.longDesc}`}
            >
              Provide a full description of this room.
            </div>
            <label className="grid">
              <textarea
                id="long-desc"
                name="longDesc"
                type=""
                maxLength={1000}
                autoFocus={true}
                placeholder="Ex: This room is furnished with a queen bed, desk and chair.  It's on the 2nd floor and has a great veiw of the ocean..."
                value={room.longDesc}
                className={`w-80 h-[275px] border-gray-400 rounded-md border-1 p-3 ${roomError.longDesc}`}
                onChange={handleChange}
              />
            </label>
          </>
        )}
        {page === 5 && (
          <>
            <div className="text-xl mt-5 mb-3">Select additional options:</div>
            <div className="w-80 mt-5 px-1">
              <div className="grid grid-cols-2 gap-x-5 gap-y-3">
                <div className="flex justify-between items-center">
                  <div>Furnished:</div>
                  <input
                    id="funished"
                    name="isFurnished"
                    type="checkbox"
                    checked={room.isFurnished}
                    className="h-5 w-5"
                    onChange={handleCheckbox}
                  />
                </div>
                <div className="flex justify-between items-center">
                  <div>Couples Ok:</div>
                  <input
                    id="couples-ok"
                    name="couplesOk"
                    type="checkbox"
                    checked={room.couplesOk}
                    className="h-5 w-5"
                    onChange={handleCheckbox}
                  />
                </div>
                <div className="flex justify-between items-center">
                  <div>Smoking Ok:</div>
                  <input
                    id="smoking-ok"
                    name="smokingOk"
                    type="checkbox"
                    checked={room.smokingOk}
                    className="h-5 w-5"
                    onChange={handleCheckbox}
                  />
                </div>
                <div className="flex justify-between items-center">
                  <div>Cannabis Ok:</div>
                  <input
                    id="cannabis-ok"
                    name="cannabisOk"
                    type="checkbox"
                    checked={room.cannabisOk}
                    className="h-5 w-5"
                    onChange={handleCheckbox}
                  />
                </div>
                <div className="flex justify-between items-center">
                  <div>Male Only:</div>
                  <input
                    id="male-only"
                    name="maleOnly"
                    type="checkbox"
                    checked={room.maleOnly}
                    className="h-5 w-5"
                    onChange={handleCheckbox}
                  />
                </div>
                <div className="flex justify-between items-center">
                  <div>Female Only:</div>
                  <input
                    id="female-only"
                    name="femaleOnly"
                    type="checkbox"
                    checked={room.femaleOnly}
                    className="h-5 w-5"
                    onChange={handleCheckbox}
                  />
                </div>
                <div className="flex justify-between items-center">
                  <div>Mixed Gender:</div>
                  <input
                    id="is-co-ed"
                    name="isCoEd"
                    type="checkbox"
                    checked={room.isCoEd}
                    className="h-5 w-5"
                    onChange={handleCheckbox}
                  />
                </div>
                <div className="flex justify-between items-center">
                  <div>Private Bath:</div>
                  <input
                    id="private-bath"
                    name="privateBath"
                    type="checkbox"
                    checked={room.privateBath}
                    className="h-5 w-5"
                    onChange={handleCheckbox}
                  />
                </div>
                <div className="flex justify-between items-center">
                  <div>Dogs Ok:</div>
                  <input
                    id="dogs-ok"
                    name="dogsOk"
                    type="checkbox"
                    checked={room.dogsOk}
                    className="h-5 w-5"
                    onChange={handleCheckbox}
                  />
                </div>
                <div className="flex justify-between items-center">
                  <div>Cats Ok:</div>
                  <input
                    id="cats-ok"
                    name="catsOk"
                    type="checkbox"
                    checked={room.catsOk}
                    className="h-5 w-5"
                    onChange={handleCheckbox}
                  />
                </div>
                <div className="flex justify-between items-center">
                  <div>Laundry:</div>
                  <input
                    id="laundry"
                    name="laundry"
                    type="checkbox"
                    checked={room.laundry}
                    className="h-5 w-5"
                    onChange={handleCheckbox}
                  />
                </div>
                <div className="flex justify-between items-center">
                  <div>Wifi:</div>
                  <input
                    id="wifi"
                    name="wifi"
                    type="checkbox"
                    checked={room.wifi}
                    className="h-5 w-5"
                    onChange={handleCheckbox}
                  />
                </div>
                <div className="flex justify-between items-center">
                  <div>LGBTQ+ Friendly:</div>
                  <input
                    id="lgbt-freindly"
                    name="lgbtFriendly"
                    type="checkbox"
                    checked={room.lgbtFriendly}
                    className="h-5 w-5"
                    onChange={handleCheckbox}
                  />
                </div>
                <div className="flex justify-between items-center">
                  <div> Off-Street Parking:</div>
                  <input
                    id="parking"
                    name="parking"
                    type="checkbox"
                    checked={room.parking}
                    className="h-5 w-5"
                    onChange={handleCheckbox}
                  />
                </div>
                <div className="flex col-span-2 justify-around items-center px-10">
                  <div>Wheelchair Accessible:</div>
                  <input
                    id="accessible"
                    name="isAccessible"
                    type="checkbox"
                    checked={room.isAccessible}
                    className="h-5 w-5"
                    onChange={handleCheckbox}
                  />
                </div>
              </div>
            </div>
          </>
        )}
        {page === pages && (
          <ImageUploader
            stateObject={room}
            setStateObject={setRoom}
            name="rooms"
          />
        )}
      </form>
      <div className="flex justify-center w-full fixed bottom-28">
        <Button
          background="bg-slate-400"
          text="text-white"
          opacity="bg-opacity-100"
          title={page === 1 ? "Cancel" : "Back"}
          height="h-10"
          width="w-24"
          borderColor="border-none"
          borderHover="hover:border-snappily-orange"
          customFunc={page === 1 ? cancelAddRoom : prevPage}
        />
        <Button
          isDisabled={isLoading}
          background="bg-snappily-orange"
          text="text-white"
          opacity="bg-opacity-100"
          title={page === pages ? "Create" : "Next"}
          height="h-10"
          width="w-24"
          borderColor="border-none"
          borderHover="hover:border-snappily-orange"
          customFunc={page === pages ? handleAddRoom : nextPage}
        />
      </div>
    </div>
  );
};

export default AddRoom;
